import { GoogleGenAI, Type, Modality } from "@google/genai";
import { ref, getBlob, getBytes } from "firebase/storage";
import { storage } from "./firebase";

// Session cache to store audio blobs and bypass CORS for newly generated stories
const audioCache = new Map<string, Blob>();
const MAX_CACHE_SIZE = 20;

export const cacheAudioBlob = (url: string, blob: Blob) => {
  if (audioCache.size >= MAX_CACHE_SIZE) {
    const firstKey = audioCache.keys().next().value;
    if (firstKey) audioCache.delete(firstKey);
  }
  audioCache.set(url, blob);
};
import { GeneratedStoryResponse } from "../types";

const STORY_THEME_DESCRIPTIONS = {
  fairytale: "Përrallë klasike (fairytale): Mbretëri magjike me pyje të thella, kështjella vezulluese, kafshë që flasin, krijesa të magjepsura dhe një moral të bukur e të thjeshtë në fund.",
  adventure: "Aventurë (adventure): Eksplorim dhe zbulim i botëve të reja. Heroi niset në një udhëtim emocionues, gjen miq të rinj, kapërcen pengesa të vogla dhe zbulon thesare ose sekrete të fshehura.",
  funny: "Humor (funny): Histori gazmore me keqkuptime qesharake, situata absurde por të padëmshme, personazhe lozonjarë, tinguj qesharakë dhe shumë të qeshura.",
  'sci-fi': "Shkencë-fiksion (sci-fi): Robotë miqësorë, anije hapësinore plot ngjyra, zbulimi i planeteve të reja me bimësi të çuditshme, pajisje inteligjente dhe kafshë shtëpiake aliene.",
  bedtime: "Gjumë të ëmbël (bedtime): Histori shumë qetësuese, ritëm i ngadaltë me fjalë të buta e përsëritëse. Atmosferë e muzgut, yjeve dhe miqve që bëhen gati për të fjetur.",
  albanian_folklore: `Folklor Shqiptar (albanian_folklore): Universi magjik i përrallave dhe legjendave shqiptare.
    1. Personazhet Heroikë: Muji & Halili (kreshnikët e maleve, fuqia nga qumështi i Zanave), Gjergj Elez Alia (sakrifica dhe dashuria për motrën), Konstandini & Doruntina (personifikimi i Besës që ngrihet nga varri), dhe "Trimi i mirë me shokë shumë" (fiton me miq besnikë me aftësi të veçanta).
    2. Bestiari Mitologjik: Zanat e Malit (luftëtare & mjekuese te krojet), Orët e Malit (shpirtra mbrojtës; nëse Ora fle, vjen rreziku), Shtojzavallet (qenie dritë-lehta që kërcejnë në lëndina), Dragoi (hero pozitiv me krahë nën sqetull që lufton Kuçedrën për shi e diell), Kuçedra (kaosi me shumë koka), dhe Gjarpri i Shtëpisë (roja mistike e familjes).
    3. Personazhet e Përrallave: E Bukura e Dheut (mençuri mistike), Gishtoja (i vogël sa një gisht por shumë i zgjuar), Kçira/Katallani (ciklopët me një sy), dhe Plaka e Dimrit (sjell dëborën duke shkundur dyshekët).
    4. Simbolet & Vlerat: Besa (instituti më i lartë moral), Mikpritja (shtëpia e Zotit dhe e mikut), Ndera & Burrëria (kod integriteti), dhe Uji i Jetës (burimi magjik shërues).
    5. Estetika & Peizazhi: Veshje (Xhubleta, Plisi/Qeleshja, Xhamadani me ari, Opingat me xhufka). Arkitektura (Kullat prej guri me frëngji, Ura e Mesit). Natyra (Bjeshkët e Namuna, Shpella e Dragoit, Syri i Kaltër, pyjet me dushk e rrap).`
};

const AGE_RULES = {
  '0-3': "Grupmosha 0-3 vjeç: Përdor fjali shumë të shkurtra, fjalë të thjeshta, tinguj onomatopeikë (si vrum-vrum, miau, etj.), dhe fokusohu te ngjyrat, format dhe veprimet bazë.",
  '3-6': "Grupmosha 3-6 vjeç: Përdor fjali mesatare dhe një fjalor të pasur por të kuptueshëm. Krijo një problem të vogël që zgjidhet lehtë, duke u fokusuar te miqësia, ndarja e gjërave dhe shprehja e ndjenjave.",
  '6-9': "Grupmosha 6-9 vjeç: Krijo një histori më të detajuar me zhvillim të personazhit, fjalor më të avancuar, një sfidë ose mister të lehtë për t'u zgjidhur, dhe mesazhe më të thella morale.",
  '9+': "Grupmosha 9+ vjeç: Krijo një histori komplekse me dialogë, zhvillim të botës, sfida reale dhe mësime jetësore të rëndësishme, duke ruajtur tonin magjik."
};

const getApiKey = () => {
  const key = process.env.GEMINI_API_KEY;
  if (key && key !== "undefined" && key !== "") return key;
  return "";
};

const retry = async <T>(fn: () => Promise<T>, retries: number = 2, delay: number = 1000): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) throw error;
    await new Promise(resolve => setTimeout(resolve, delay));
    return retry(fn, retries - 1, delay * 2);
  }
};

export const generateStory = async (
  characterName: string,
  prompt: string,
  photoBase64: string | null,
  storyType: string = 'fairytale',
  ageCategory: string = '3-6'
): Promise<GeneratedStoryResponse> => {
  const apiKey = getApiKey();
  if (!apiKey) throw new Error("Mungon çelësi i API (GEMINI_API_KEY). Ju lutem shtoni atë te Secrets.");
  
  const ai = new GoogleGenAI({ apiKey });
  
  const themeGuidance = STORY_THEME_DESCRIPTIONS[storyType as keyof typeof STORY_THEME_DESCRIPTIONS] || STORY_THEME_DESCRIPTIONS.fairytale;
  const ageGuidance = AGE_RULES[ageCategory as keyof typeof AGE_RULES] || AGE_RULES['3-6'];

  const fullPrompt = `
    Je një tregimtar profesionist për fëmijë. Krijo një përrallë magjike në gjuhën shqipe me saktësisht 6 pjesë.
    
    RREGULLI I MOSHËS:
    ${ageGuidance}
    
    Nëse është ngarkuar një foto, analizoje atë me kujdes. Përshkruaj tiparet kryesore të fëmijës (ngjyra e flokëve, sytë, veshja, ndonjë aksesor) dhe përdore këtë përshkrim për të krijuar një personazh që i ngjan fëmijës në foto.
    
    Për çdo pjesë, krijo një 'imagePrompt' në anglisht (stili: children's book illustration, soft colors, magical lighting, borderless, full bleed, edge-to-edge artwork, no white borders, no frames). 
    
    RREGULLI I KONSISTENCËS VIZUALE: ÇDO 'imagePrompt' DUHET të nisë me të njëjtin përshkrim të detajuar fizik të personazhit kryesor për të siguruar që personazhi duket identik në të 6 ilustrimet. 
    Gjithmonë përdor imazhe të ilustruara (illustrated images), jo pjesë reale nga fotoja e ngarkuar. Mbaj të njëjtin stil në të 6 fotot që gjeneron.
    Mos përfshi korniza, tekste, korniza të bardha, apo flluska fjalësh në përshkrimet e imazheve.
    
    Tema: ${themeGuidance}
    Personazhi: ${characterName}. 
    Ideja: ${prompt}. 
    Tema e zgjedhur: ${storyType}.
    
    PËRGJIGJU VETËM NË FORMATIN JSON:
    {
      "title": "Titulli",
      "parts": [
        { "text": "Teksti i pjesës...", "imagePrompt": "Detailed visual description of the character and the scene, borderless, full bleed, edge-to-edge artwork, no white borders..." }
      ]
    }
  `;

  const parts: any[] = [];
  if (photoBase64) {
    // Sigurohemi që kemi vetëm pjesën base64 pa prefix-in data:image/...
    const cleanBase64 = photoBase64.includes(',') ? photoBase64.split(',')[1] : photoBase64;
    parts.push({ inlineData: { mimeType: 'image/jpeg', data: cleanBase64 } });
  }
  parts.push({ text: fullPrompt });

  console.log("Starting story generation for:", characterName);
  
  const tryGenerateStory = async (modelName: string, useSchema: boolean = true) => {
    const config: any = {
      responseMimeType: "application/json",
    };

    if (useSchema) {
      config.responseSchema = {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          parts: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                text: { type: Type.STRING },
                imagePrompt: { type: Type.STRING }
              },
              required: ["text", "imagePrompt"]
            }
          }
        },
        required: ["title", "parts"]
      };
    }

    const response = await ai.models.generateContent({
      model: modelName,
      contents: [{ role: "user", parts }],
      config
    });

    const text = response.text;
    if (!text) throw new Error("Modeli nuk ktheu asnjë përgjigje.");
    
    const cleanJson = text.replace(/```json/g, '').replace(/```/g, '').trim();
    try {
      const data = JSON.parse(cleanJson);
      if (!data.parts || !Array.isArray(data.parts)) {
        throw new Error("Struktura e përrallës është e pasaktë (mungojnë pjesët).");
      }
      return data as GeneratedStoryResponse;
    } catch (parseError) {
      console.error("JSON Parse Error:", parseError, "Raw text:", text);
      throw new Error("Dështoi interpretimi i përgjigjes JSON.");
    }
  };

  return retry(async () => {
    try {
      console.log("Duke provuar gjenerimin me gemini-3-flash-preview...");
      return await tryGenerateStory('gemini-3-flash-preview');
    } catch (error: any) {
      console.warn("Dështoi modeli gemini-3-flash-preview, duke provuar gemini-flash-latest...", error.message);
      try {
        // Përdorim gemini-flash-latest si fallback i parë pasi është shumë i qëndrueshëm
        return await tryGenerateStory('gemini-flash-latest');
      } catch (fallbackError: any) {
        console.warn("Dështoi edhe gemini-flash-latest, duke provuar pa schema...", fallbackError.message);
        try {
          // Provon pa schema nëse schema shkakton gabim 500
          return await tryGenerateStory('gemini-3-flash-preview', false);
        } catch (noSchemaError: any) {
          console.warn("Dështoi edhe pa schema, duke provuar gemini-3.1-pro-preview...", noSchemaError.message);
          try {
            return await tryGenerateStory('gemini-3.1-pro-preview');
          } catch (proError: any) {
            console.error("Të gjitha modelet e tekstit dështuan:", proError);
            if (proError.message?.includes('500')) {
              throw new Error("Serveri i Inteligjencës Artificiale po përjeton mbingarkesë (Gabim 500). Ju lutem provoni përsëri pas pak ose provoni pa ngarkuar foto.");
            }
            throw proError;
          }
        }
      }
    }
  }, 2, 3000);
};

const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(errorMessage)), timeoutMs)
    ),
  ]);
};

export const generateImage = async (prompt: string, referencePhoto?: string | null): Promise<string> => {
  const apiKey = getApiKey();
  if (!apiKey) throw new Error("Mungon çelësi i API.");
  
  const ai = new GoogleGenAI({ apiKey });
  
  const parts: any[] = [{ text: `Children's book illustration style, soft colors, magical lighting, borderless, full bleed, edge-to-edge artwork, no white borders, no frames: ${prompt}` }];
  if (referencePhoto) {
    parts.unshift({ inlineData: { mimeType: 'image/jpeg', data: referencePhoto } });
  }

  const tryModel = async (modelName: string) => {
    const response = await ai.models.generateContent({
      model: modelName,
      contents: { parts },
      config: { 
        imageConfig: { 
          aspectRatio: "1:1",
          imageSize: "1K"
        } 
      }
    });

    const candidates = response.candidates;
    if (candidates && candidates.length > 0 && candidates[0].content && candidates[0].content.parts) {
      for (const part of candidates[0].content.parts) {
        if (part.inlineData) {
          const rawBase64 = part.inlineData.data;
          if (rawBase64) {
            // Compress the generated image to save space
            return await compressImageBase64(rawBase64, 800, 0.75);
          }
        }
      }
    }
    throw new Error("Imazhi nuk u gjet në përgjigje.");
  };

  return retry(async () => {
    try {
      // Provon modelin e parë me timeout 45 sekonda (rritur nga 30s)
      return await withTimeout(
        tryModel('gemini-2.5-flash-image'),
        45000,
        "Koha e pritjes për imazhin mbaroi (45s)"
      );
    } catch (error: any) {
      console.warn("Dështoi modeli i parë ose mbaroi koha, duke provuar modelin fallback...", error.message);
      
      // Nëse është gabim 403, mund të jetë problem me lejet e modelit
      if (error.message?.includes('403') || error.message?.includes('permission')) {
        console.warn("Gabim 403 i detektuar, duke provuar modelin fallback...");
      }

      try {
        // Fallback te modeli tjetër me timeout 45 sekonda
        return await withTimeout(
          tryModel('gemini-3.1-flash-image-preview'),
          45000,
          "Edhe modeli fallback dështoi pas 45s"
        );
      } catch (fallbackError: any) {
        console.error("Të dy modelet dështuan:", fallbackError.message);
        
        // Përmirësojmë mesazhin e gabimit për përdoruesin
        if (fallbackError.message?.includes('403') || fallbackError.message?.includes('permission')) {
          throw new Error("Nuk keni leje për të përdorur modelet e imazheve. Ju lutem kontrolloni çelësin tuaj të API ose zgjidhni një çelës të paguar (Paid Key) nëse jeni në platformë.");
        }
        
        throw fallbackError;
      }
    }
  }, 1, 2000); 
};

export const getStoryAudio = async (text: string, voiceType: 'female' | 'male' = 'female'): Promise<string> => {
  const apiKey = getApiKey();
  if (!apiKey) throw new Error("Mungon çelësi i API.");
  
  const ai = new GoogleGenAI({ apiKey });
  const voiceName = voiceType === 'female' ? 'Kore' : 'Puck';
  
  const truncatedText = text.length > 10000 ? text.substring(0, 10000) + "..." : text;
  
  const persona = voiceType === 'female' ? 'mama që i lexon një përrallë fëmijës së saj' : 'babi që i lexon një përrallë fëmijës së tij';
  
  return retry(async () => {
    try {
      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash-preview-tts",
        contents: [{ parts: [{ text: `Je një ${persona}. Lexo këtë tregim në gjuhën shqipe me zë të qartë, të ngrohtë dhe plot dashuri: ${truncatedText}` }] }],
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: { voiceName }
            }
          },
        },
      });

      const base64Audio = response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
      if (!base64Audio) throw new Error("Gjenerimi i audios dështoi.");
      return base64Audio;
    } catch (error: any) {
      console.error("Audio Generation Attempt Failed:", error);
      throw error;
    }
  }, 2, 2000);
};

export const getWavHeader = (pcmLength: number): ArrayBuffer => {
  const header = new ArrayBuffer(44);
  const view = new DataView(header);
  const writeString = (v: DataView, o: number, s: string) => { 
    for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); 
  };
  
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + pcmLength, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, 24000, true);
  view.setUint32(28, 48000, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, pcmLength, true);
  return header;
};

export const getAudioBlob = async (audioSource: string): Promise<Blob> => {
  // Check session cache first
  if (audioCache.has(audioSource)) {
    return audioCache.get(audioSource)!;
  }

  if (audioSource.startsWith('data:')) {
    try {
      const parts = audioSource.split(',');
      if (parts.length < 2) throw new Error("Format i pavlefshëm i audios (data URL).");
      const header = parts[0];
      const base64 = parts[1];
      
      const headerParts = header.split(':');
      if (headerParts.length < 2) throw new Error("Header i pavlefshëm i audios.");
      const mime = headerParts[1].split(';')[0];
      
      const binary = window.atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: mime });
      audioCache.set(audioSource, blob);
      return blob;
    } catch (e) {
      console.error("Gabim gjatë konvertimit të data URL:", e);
      throw e;
    }
  }

  if (audioSource.startsWith('blob:')) {
    try {
      const response = await fetch(audioSource);
      const blob = await response.blob();
      audioCache.set(audioSource, blob);
      return blob;
    } catch (e) {
      console.error("Gabim gjatë marrjes së blob URL:", e);
      throw e;
    }
  }

  if (audioSource.startsWith('http')) {
    // Try fetch first as it's often faster and handles public URLs well
    try {
      console.log("Duke provuar fetch direkt për audion...");
      const response = await fetch(audioSource, { 
        mode: 'cors',
        cache: 'force-cache'
      });
      if (response.ok) {
        const blob = await response.blob();
        audioCache.set(audioSource, blob);
        return blob;
      }
    } catch (fetchErr) {
      console.warn("Fetch direkt dështoi (ndoshta CORS), duke provuar SDK:", fetchErr);
    }

    // Handle Firebase Storage URLs via SDK if fetch failed
    if (audioSource.includes('firebasestorage.googleapis.com')) {
      try {
        console.log("Duke shkarkuar nga Storage via SDK...");
        const storageRef = ref(storage, audioSource);
        const bytes = await getBytes(storageRef);
        const blob = new Blob([bytes], { type: 'audio/wav' });
        audioCache.set(audioSource, blob);
        return blob;
      } catch (sdkError: any) {
        console.warn("Firebase SDK getBytes dështoi:", sdkError.code, sdkError.message);
        
        try {
          const storageRef = ref(storage, audioSource);
          const blob = await getBlob(storageRef);
          audioCache.set(audioSource, blob);
          return blob;
        } catch (innerErr) {
          console.warn("Firebase SDK getBlob dështoi gjithashtu");
        }
      }
    }
    
    throw new Error("Dështoi shkarkimi i audios. Ky është një problem i njohur me sigurinë e shfletuesit (CORS).");
  }

  // Handle raw base64 PCM (legacy or direct from API)
  try {
    const wavBase64 = pcmToWavBase64(audioSource);
    const binary = window.atob(wavBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: 'audio/wav' });
  } catch (pcmErr) {
    console.error("Konvertimi PCM dështoi:", pcmErr);
    throw pcmErr;
  }
};

export const playAudio = async (audioSource: string, onEnded: () => void): Promise<{ 
  close: () => Promise<void>, 
  pause: () => void, 
  resume: () => Promise<void> 
}> => {
  if (!audioSource) throw new Error("Burimi i audios mungon.");

  let blobUrl = '';
  let audio: HTMLAudioElement | null = new Audio();
  let isClosed = false;

  // Pre-set some properties for faster loading
  audio.preload = "auto";

  const controller = {
    pause: () => {
      if (audio) audio.pause();
    },
    resume: async () => {
      if (audio) await audio.play();
    },
    close: async () => {
      isClosed = true;
      if (audio) {
        audio.pause();
        audio.src = "";
        audio.oncanplay = null;
        audio.oncanplaythrough = null;
        audio.onended = null;
        audio.onerror = null;
        audio = null;
      }
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        blobUrl = '';
      }
    }
  };

  return new Promise((resolve, reject) => {
    if (!audio) return reject(new Error("Dështoi krijimi i elementit Audio."));

    const setupListeners = (targetAudio: HTMLAudioElement) => {
      targetAudio.oncanplay = async () => {
        if (isClosed) return;
        try {
          console.log("Audio is ready to play (oncanplay)");
          // Remove listener to prevent multiple triggers
          targetAudio.oncanplay = null;
          targetAudio.oncanplaythrough = null;
          await targetAudio.play();
          resolve(controller);
        } catch (e) {
          console.error("Audio play() dështoi:", e);
          if (!isClosed) reject(e);
        }
      };

      targetAudio.oncanplaythrough = async () => {
        if (isClosed) return;
        // If oncanplay already triggered, this might be redundant but safe
        if (targetAudio.oncanplay === null) return; 
        
        try {
          console.log("Audio is ready to play through");
          targetAudio.oncanplay = null;
          targetAudio.oncanplaythrough = null;
          await targetAudio.play();
          resolve(controller);
        } catch (e) {
          console.error("Audio play() dështoi (through):", e);
          if (!isClosed) reject(e);
        }
      };

      targetAudio.onended = () => {
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
          blobUrl = '';
        }
        onEnded();
      };

      targetAudio.onerror = () => {
        if (isClosed) return;
        const err = targetAudio.error;
        console.error("Audio error:", err?.code, err?.message);
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
          blobUrl = '';
        }
        onEnded();
        reject(new Error(`Gabim gjatë ngarkimit të audios: ${err?.code} - ${err?.message}`));
      };
    };

    setupListeners(audio);

    // Start loading logic
    const startLoading = async () => {
      try {
        if (audioSource.startsWith('data:') || audioSource.startsWith('blob:')) {
          if (audio) audio.src = audioSource;
        } else if (audioSource.startsWith('http')) {
          // For Firebase URLs, we prefer getting a blob via SDK to handle CORS/Auth better
          if (audioSource.includes('firebasestorage.googleapis.com')) {
            try {
              console.log("Duke provuar shkarkimin e audios via SDK...");
              const blob = await getAudioBlob(audioSource);
              if (isClosed || !audio) return;
              blobUrl = URL.createObjectURL(blob);
              audio.src = blobUrl;
            } catch (blobErr) {
              console.warn("Dështoi marrja e blob-it via SDK, duke provuar src direkte:", blobErr);
              if (isClosed || !audio) return;
              // Add a cache buster to avoid potential stale CORS issues
              const cacheBuster = audioSource.includes('?') ? `&cb=${Date.now()}` : `?cb=${Date.now()}`;
              audio.src = audioSource + cacheBuster;
            }
          } else {
            if (audio) audio.src = audioSource;
          }
        } else {
          // Raw PCM
          const blob = await getAudioBlob(audioSource);
          if (isClosed || !audio) return;
          blobUrl = URL.createObjectURL(blob);
          audio.src = blobUrl;
        }
        
        if (audio) audio.load();
      } catch (err) {
        if (!isClosed) reject(err);
      }
    };

    startLoading();
  });
};

function decode(base64: string): Uint8Array {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

async function decodeAudioData(data: Uint8Array, ctx: AudioContext, sampleRate: number, numChannels: number): Promise<AudioBuffer> {
  // Sigurohemi që përdorim offset dhe gjatësi të saktë për Int16Array (2 bytes për sample)
  // Nëse offset-i nuk është i pjesëtueshëm me 2, krijojmë një kopje të rreshtuar
  let buffer = data.buffer;
  let offset = data.byteOffset;
  if (offset % 2 !== 0) {
    const aligned = new Uint8Array(data.byteLength);
    aligned.set(data);
    buffer = aligned.buffer;
    offset = 0;
  }

  const length = Math.floor(data.byteLength / 2);
  const dataInt16 = new Int16Array(buffer, offset, length);
  const frameCount = Math.floor(dataInt16.length / numChannels);
  const audioBuffer = ctx.createBuffer(numChannels, frameCount, sampleRate);
  
  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = audioBuffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return audioBuffer;
}

export const pcmToWavBase64 = (pcmBase64: string): string => {
  let pcmData = decode(pcmBase64);
  
  // Sigurohemi që gjatësia është çift për 16-bit PCM
  if (pcmData.length % 2 !== 0) {
    const padded = new Uint8Array(pcmData.length + 1);
    padded.set(pcmData);
    pcmData = padded;
  }

  const header = getWavHeader(pcmData.length);
  const wavBuffer = new Uint8Array(header.byteLength + pcmData.length);
  wavBuffer.set(new Uint8Array(header), 0);
  wavBuffer.set(pcmData, header.byteLength);
  
  // Konvertim efikas në base64 për buffer-a të mëdhenj
  let binary = '';
  const chunk = 8192;
  for (let i = 0; i < wavBuffer.length; i += chunk) {
    // @ts-ignore - apply works with Uint8Array subarray
    binary += String.fromCharCode.apply(null, wavBuffer.subarray(i, i + chunk));
  }
  return window.btoa(binary);
};

export const compressImageBase64 = async (base64: string, maxSize: number = 800, quality: number = 0.75): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxSize) {
          height *= maxSize / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width *= maxSize / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(`data:image/png;base64,${base64}`);
    img.src = base64.startsWith('data:') ? base64 : `data:image/png;base64,${base64}`;
  });
};

export const downloadAudioAsWav = async (audioSource: string, filename: string) => {
  let url = '';
  try {
    const blob = await getAudioBlob(audioSource);
    url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.wav`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (err) {
    console.error("Gabim gjatë shkarkimit të audios:", err);
    // Final fallback for http URLs - open in new tab to let browser handle it
    if (audioSource.startsWith('http')) {
      const a = document.createElement('a');
      a.href = audioSource;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
    }
  } finally {
    if (url) setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};